<template>
  <div>
    <v-dialog
      v-for="dialog in dialogs"
      :key="dialog.id"
      v-model="dialog.show"
      :max-width="dialog.maxWidth || 400"
      :persistent="dialog.persistent !== false"
      :aria-label="dialog.title || getDefaultTitle(dialog)"
      @update:model-value="(value) => onDialogUpdate(dialog.id, value)"
    >
      <v-card>
        <v-card-title v-if="dialog.title || dialog.icon" class="text-h6 d-flex align-center">
          <v-icon v-if="dialog.icon" :color="dialog.iconColor || getDefaultIconColor(dialog)" class="mr-2">
            {{ dialog.icon }}
          </v-icon>
          {{ dialog.title || getDefaultTitle(dialog) }}
        </v-card-title>
        
        <v-card-text v-if="dialog.message || dialog.content">
          <p v-if="dialog.message">{{ dialog.message }}</p>
          <div v-if="dialog.content" v-html="dialog.content"></div>
          <p v-if="dialog.subtitle" class="text-caption text-medium-emphasis mt-2">
            {{ dialog.subtitle }}
          </p>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer />
          <v-btn 
            v-if="dialog.cancelText || dialog.showCancel"
            variant="text" 
            @click="onCancel(dialog)"
            :disabled="dialog.loading"
          >
            {{ dialog.cancelText || 'Cancel' }}
          </v-btn>
          <v-btn 
            :color="dialog.confirmColor || getDefaultConfirmColor(dialog)"
            :variant="dialog.confirmVariant || 'text'"
            @click="onConfirm(dialog)"
            :loading="dialog.loading"
            :disabled="dialog.loading"
          >
            {{ dialog.confirmText || getDefaultConfirmText(dialog) }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { useDialogManager } from '../../utils/DialogManager'

const { dialogs, closeDialog } = useDialogManager()

// Dialog types and their defaults
const dialogTypes = {
  error: {
    icon: 'error',
    iconColor: 'error',
    title: 'Error',
    confirmText: 'OK',
    confirmColor: 'primary'
  },
  warning: {
    icon: 'warning',
    iconColor: 'warning', 
    title: 'Warning',
    confirmText: 'OK',
    confirmColor: 'primary'
  },
  info: {
    icon: 'info',
    iconColor: 'info',
    title: 'Information',
    confirmText: 'OK',
    confirmColor: 'primary'
  },
  success: {
    icon: 'check_circle',
    iconColor: 'success',
    title: 'Success',
    confirmText: 'OK',
    confirmColor: 'primary'
  },
  confirm: {
    icon: 'help',
    iconColor: 'primary',
    title: 'Confirm',
    confirmText: 'Yes',
    confirmColor: 'primary',
    showCancel: true,
    cancelText: 'No'
  }
}

const getDefaultIconColor = (dialog) => {
  const type = dialog.type || 'info'
  return dialogTypes[type]?.iconColor || 'primary'
}

const getDefaultTitle = (dialog) => {
  const type = dialog.type || 'info'
  return dialogTypes[type]?.title || 'Dialog'
}

const getDefaultConfirmText = (dialog) => {
  const type = dialog.type || 'info'
  return dialogTypes[type]?.confirmText || 'OK'
}

const getDefaultConfirmColor = (dialog) => {
  const type = dialog.type || 'info'
  return dialogTypes[type]?.confirmColor || 'primary'
}

const onDialogUpdate = (id, value) => {
  if (!value) {
    closeDialog(id)
  }
}

const onConfirm = async (dialog) => {
  if (dialog.onConfirm) {
    dialog.loading = true
    try {
      const result = await dialog.onConfirm()
      if (result !== false) {
        closeDialog(dialog.id)
      }
    } catch (error) {
      console.error('Dialog confirm handler error:', error)
    } finally {
      dialog.loading = false
    }
  } else {
    closeDialog(dialog.id)
  }
}

const onCancel = async (dialog) => {
  if (dialog.onCancel) {
    dialog.loading = true
    try {
      await dialog.onCancel()
    } catch (error) {
      console.error('Dialog cancel handler error:', error)
    } finally {
      dialog.loading = false
    }
  }
  closeDialog(dialog.id)
}
</script>

